import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { GetPageDataQuery, GetSiteDataQuery, GetHomeDataQuery, GetAboutDataQuery, GetPortfolioDataQuery, GetTestimonialDataQuery, GetContactDataQuery, GetBlogDataQuery } from '../generated/graphql';
import { GetAboutDataGqlService, GetBlogDataGqlService, GetContactDataGqlService, GetHomeDataGqlService, GetPageDataGqlService, GetPortfolioDataGqlService, GetSiteDataGqlService, GetTestimonialDataGqlService } from './index';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private readonly apollo = inject(Apollo);
  private readonly getPageDataGqlService = inject(GetPageDataGqlService);
  private readonly getSiteDataGqlService = inject(GetSiteDataGqlService);
  private readonly getHomeDataGqlService = inject(GetHomeDataGqlService);
  private readonly getAboutDataGqlService = inject(GetAboutDataGqlService);
  private readonly getPortfolioDataGqlService = inject(GetPortfolioDataGqlService);
  private readonly getTestimonialDataGqlService = inject(GetTestimonialDataGqlService);
  private readonly getContactDataGqlService = inject(GetContactDataGqlService);
  private readonly getBlogDataGqlService = inject(GetBlogDataGqlService);

  getPageData(): Observable<any> {
    return this.apollo.query<GetPageDataQuery>({ 
      query: this.getPageDataGqlService.document,
      errorPolicy: 'all'
    }).pipe(
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('getPageData failed', err);
        return throwError(() => err);
      })
    );
  }

  getSiteData(): Observable<any> {
    return this.apollo.query<GetSiteDataQuery>({ 
      query: this.getSiteDataGqlService.document,
      errorPolicy: 'all'
    }).pipe(
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('getSiteData failed', err);
        return throwError(() => err);
      })
    );
  }

  // Convenience helper that returns the *data* (unwrapped) for consumers
  getSiteDataData(): Observable<GetSiteDataQuery> {
    return this.getSiteData().pipe(map((res: any) => res.data as GetSiteDataQuery));
  }

  getHomeData(): Observable<any> {
    return this.apollo.query<GetHomeDataQuery>({ 
      query: this.getHomeDataGqlService.document,
      errorPolicy: 'all'
    }).pipe(
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('getHomeData failed', err);
        return throwError(() => err);
      })
    );
  }

  getAboutData(): Observable<any> {
    return this.apollo.query<GetAboutDataQuery>({ 
      query: this.getAboutDataGqlService.document,
      errorPolicy: 'all'
    }).pipe(
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('getAboutData failed', err);
        return throwError(() => err);
      })
    );
  }

  getPortfolioData(): Observable<any> {
    return this.apollo.query<GetPortfolioDataQuery>({ 
      query: this.getPortfolioDataGqlService.document,
      errorPolicy: 'all'
    }).pipe(
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('getPortfolioData failed', err);
        return throwError(() => err);
      })
    );
  }

  getTestimonialData(): Observable<any> {
    return this.apollo.query<GetTestimonialDataQuery>({ 
      query: this.getTestimonialDataGqlService.document,
      errorPolicy: 'all'
    }).pipe(
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('getTestimonialData failed', err);
        return throwError(() => err);
      })
    );
  }

  getContactData(): Observable<any> {
    return this.apollo.query<GetContactDataQuery>({ 
      query: this.getContactDataGqlService.document,
      errorPolicy: 'all'
    }).pipe(
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('getContactData failed', err);
        return throwError(() => err);
      })
    );
  }

  getBlogData(): Observable<any> {
    return this.apollo.query<GetBlogDataQuery>({ 
      query: this.getBlogDataGqlService.document,
      errorPolicy: 'all'
    }).pipe(
      catchError((err) => {
        // eslint-disable-next-line no-console
        console.error('getBlogData failed', err);
        return throwError(() => err);
      })
    );
  }

  // Convenience helper that returns unwrapped data so callers don't need to
  // access `result.data`.
  getBlogDataData(): Observable<GetBlogDataQuery> {
    return this.getBlogData().pipe(map((res: any) => res.data as GetBlogDataQuery));
  }
}
